import React, { useState, useEffect, useMemo } from 'react';
import './Dashboard.scss';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { formatDateWith12HourTime, formatDateTime12Hour } from '../../shared/utils/dateUtils';
import { getWeeklyDailySummaries } from '../../core/API/workplace-daily-summaries';
import {
	CircularProgress,
	Grid,
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from '@mui/material';
import { getCategorizedToDos } from '../../core/API/todo';
import { getCategorizedChecklistScheduleEvents } from '../../core/API/checklist';
import { LocalEvents } from '../../shared/components/LocalEvents';
import { LocalEvent } from '../../core/API/local-events';
import { HotGame } from '../../core/API/hotGames';
import EventDetailView from '../../shared/components/EventDetailView/EventDetailView';
import { usePermissionCheck } from '../../core/hooks/usePermissionCheck';
import { useUserAssignmentGroupIds } from '../../core/hooks/useUserAssignmentGroupIds';
import { useWorkplaceUser } from '../../module/documents/utils';
import { useUserData } from '../../store/useUserData';
import { isChecklistEventAssignedToCurrentUser } from '../../core/utils/checklistEventAssignment';
import { resolveOutstandingTodosForDisplay } from '../../core/utils/todoAssignment';
import { SecurityRolePermission } from '../../tests/services/permissionService';
import { resolveDashboardVisibility } from './utils/dashboardWidgets';
import { getCurrentUserId } from './utils/homePage';

import HotGames from '../../components/featured/hot-games/HotGames';

const formatDateTime = (dateString: string) => {
	return formatDateWith12HourTime(dateString);
};

const createDefaultSalesData = () =>
	Array.from({ length: 7 }, (_, i) => {
		const date = new Date();
		date.setDate(date.getDate() - (7 - i));
		return {
			day: date.toLocaleDateString('en-US', { weekday: 'short' }),
			sales: 0,
			customers: 0,
		};
	});

// Interface for checklist events
interface ChecklistScheduleEvent {
	id: number;
	checklistTemplate: {
		label: string;
		version: number;
		type?: string;
	};
	checklistSchedule?: {
		id: number;
		assignmentType: string;
		assignee?: {
			id: number;
			firstName: string;
			middleName?: string;
			lastName: string;
		} | null;
		group?: {
			id: number;
			label: string;
		} | null;
	};
	workplace?: {
		id: number;
		name: string;
	};
	organization?: {
		id: number;
		name: string;
	} | null;
	scheduledDate: string;
	scheduledTime?: string;
	scheduledDateFormatted?: string;
	scheduledTimeFormatted?: string;
	status: string;
	completedBy: any;
	completedAt: any;
}

interface Todo {
	id: number;
	description?: string;
	label?: string;
	name?: string;
	title?: string;
	note?:
		| string
		| {
				message?: string;
				description?: string;
		  };
	status?: {
		name?: string;
		label?: string;
	};
	priority?: {
		name?: string;
		label?: string;
	};
	category?: {
		name?: string;
		label?: string;
		title?: string;
	} | string | null;
	user?: {
		id: number;
		firstName: string;
		middleName?: string;
		lastName: string;
	};
	dueDate?: string;
	due_date?: string;
	dueTime?: string;
	pastDue?: boolean;
	assignedGroup?: { id?: number; label?: string };
	assigned?: string;
}

const Dashboard = () => {
	const { hasModuleViewPermission } = usePermissionCheck();
	const { userOrg } = useWorkplaceUser();
	const { currentUserRole } = useUserData();
	const products = useMemo(() => userOrg?.products || [], [userOrg?.products]);

	const rolePermissions = useMemo(
		() => (currentUserRole?.securityRolePermission ?? []) as SecurityRolePermission[],
		[currentUserRole],
	);

	const visibility = React.useMemo(
		() => resolveDashboardVisibility(rolePermissions, products),
		[rolePermissions, products],
	);

	const canViewDashboard = hasModuleViewPermission('Dashboard');
	const canViewChecklistAll = visibility.canViewChecklistAll;
	const canViewChecklistMy = visibility.canViewChecklistMy;
	const canViewToDoAll = visibility.canViewTodoAll;
	const canViewToDoMy = visibility.canViewTodoMy;
	const canViewSalesChart = visibility.canViewSalesChart;
	const canViewLocalEvents = visibility.canViewLocalEvents;
	const canViewHotGames = visibility.canViewHotGames;
	const canViewChecklistWidget = canViewChecklistAll || canViewChecklistMy;
	const canViewTodoWidget = canViewToDoAll || canViewToDoMy;
	const workplaceId = Number(localStorage.getItem('workplace-id') || 1);
	const currentUserId = getCurrentUserId();
	const currentSecurityRoleId = currentUserRole?.id ?? null;
	const needsAssignmentGroups = canViewChecklistWidget || canViewTodoWidget;

	const { userGroupIds, isLoading: groupsLoading } = useUserAssignmentGroupIds(
		workplaceId,
		needsAssignmentGroups,
	);

	const [upcomingChecklists, setUpcomingChecklists] = useState<ChecklistScheduleEvent[]>([]);
	const [allOutstandingTodos, setAllOutstandingTodos] = useState<Todo[]>([]);
	const [loading, setLoading] = useState(true);

	// Event detail view state
	const [selectedEvent, setSelectedEvent] = useState<LocalEvent | HotGame | null>(null);
	const [eventType, setEventType] = useState<'local-event' | 'hot-game' | null>(null);
	const [showEventDetail, setShowEventDetail] = useState(false);

	// Helper function to get description from various possible fields
	const getDescriptionDisplay = (todo: Todo): string => {
		const noteValue =
			typeof todo.note === 'string' ? todo.note : todo.note?.message || todo.note?.description;

		const candidates = [todo?.description, todo?.label, todo?.name, todo?.title, noteValue];
		return candidates.find((v) => typeof v === 'string' && v.trim() !== '') || 'N/A';
	};

	// Helper function to format todo date with time
	const formatTodoDateWithTime = (todo: Todo): string => {
		const dueDateRaw = todo.due_date ?? todo.dueDate;
		const dueTime = todo.dueTime;
		return formatDateTime12Hour(dueDateRaw, dueTime);
	};

	// Helper function to get assigned display for checklist
	const getChecklistAssignedDisplay = (checklist: ChecklistScheduleEvent): string => {
		const schedule = checklist.checklistSchedule;
		if (!schedule) return 'N/A';

		// If assignment type is group, show group label
		if (schedule.assignmentType === 'group') {
			return schedule.group?.label || 'Unassigned Group';
		}

		// If assignment type is assignee, show assignee's firstName and lastName
		if (schedule.assignmentType === 'assignee') {
			return schedule.assignee
				? `${schedule.assignee.firstName} ${schedule.assignee.lastName}`.trim()
				: 'Unassigned';
		}

		// Fallback for any other assignment type
		return 'Unassigned';
	};

	// Helper function to get owner display for checklist
	const getChecklistOwnerDisplay = (checklist: ChecklistScheduleEvent): string => {
		if (checklist.checklistTemplate?.type === 'organization') {
			return checklist.organization?.name || 'Organization';
		}
		return checklist.workplace?.name || 'Workplace';
	};

	       // Helper function to get assigned display for todo (matches Todo module logic)
	       const getTodoAssignedDisplay = (todo: Todo): string => {
		       // Check if user exists and is not null
		       if (todo?.user && todo.user !== null) {
			       const parts = [todo.user.firstName, todo.user.middleName, todo.user.lastName].filter(
				       (p) => !!p && p.trim() !== ''
			       );
			       return parts.join(' ') || 'Unknown User';
		       } else if (todo.assignedGroup) {
		       return todo.assignedGroup.label || 'Group Assignment';
	       } else if (todo.assigned) {
		       return todo.assigned;
		       } else {
			       return 'Unassigned';
		       }
	       };

	// Helper function to get status display for todo
	const getTodoStatusDisplay = (todo: Todo): string => {
		return todo.status?.label || todo.status?.name || 'N/A';
	};

	const getTodoCategoryDisplay = (todo: Todo): string => {
		if (!todo.category) return 'Uncategorized';
		if (typeof todo.category === 'string') return todo.category;
		return todo.category.label || todo.category.name || todo.category.title || 'Uncategorized';
	};

	// Event click handlers
	const handleLocalEventClick = (event: LocalEvent) => {
		setSelectedEvent(event);
		setEventType('local-event');
		setShowEventDetail(true);
	};

	const handleHotGameClick = (game: HotGame) => {
		setSelectedEvent(game);
		setEventType('hot-game');
		setShowEventDetail(true);
	};

	const handleCloseEventDetail = () => {
		setShowEventDetail(false);
		setSelectedEvent(null);
		setEventType(null);
	};

	// Helper function to check if checklist is assigned to current user or their groups
	const isAssignedToCurrentUser = (checklist: ChecklistScheduleEvent): boolean =>
		isChecklistEventAssignedToCurrentUser(
			checklist,
			currentUserId,
			userGroupIds,
			currentSecurityRoleId,
		);

	// Filter checklists based on permissions
	const checklistsToShow = canViewChecklistAll
		? upcomingChecklists
		: canViewChecklistMy
			? upcomingChecklists.filter(isAssignedToCurrentUser)
			: [];
	// ('Checklists to show:', checklistsToShow);
	// Fetch checklist and todo data when component mounts
	useEffect(() => {
		if (needsAssignmentGroups && groupsLoading) {
			return;
		}

		const fetchData = async () => {
			try {
				const currentDate = new Date().toISOString().split('T')[0];

				// Fetch checklists using ChecklistService
				const checklistData = await getCategorizedChecklistScheduleEvents(currentDate, workplaceId);

				// Fetch todos using categorized API
				const todosResponse = await getCategorizedToDos({ workplaceId, page: 1, itemsPerPage: 100 });
				const myTodosData = Array.isArray(todosResponse?.my_todos) ? todosResponse.my_todos : [];
				const outstandingTodosData = Array.isArray(todosResponse?.outstanding_todos)
					? todosResponse.outstanding_todos
					: [];
				const allOutstandingTodosData = resolveOutstandingTodosForDisplay({
					canViewToDoAll,
					canViewToDoMy,
					myTodos: myTodosData,
					outstandingTodos: outstandingTodosData,
					currentUserId,
					userGroupIds,
					currentSecurityRoleId,
				}) as Todo[];

				// Update state
				setUpcomingChecklists(checklistData?.myOutstanding || []);
				setAllOutstandingTodos(allOutstandingTodosData);
			} catch (error) {
				console.error('Error fetching data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [
		workplaceId,
		canViewToDoAll,
		canViewToDoMy,
		canViewChecklistAll,
		canViewChecklistMy,
		canViewChecklistWidget,
		canViewTodoWidget,
		currentUserId,
		currentSecurityRoleId,
		userGroupIds,
		needsAssignmentGroups,
		groupsLoading,
	]);

	const [salesData, setSalesData] = useState<{ day: string; sales: number; customers: number }[]>(
		createDefaultSalesData
	);

	// Fetch sales and traffic data
	useEffect(() => {
		if (!canViewSalesChart) return;

		const fetchSalesData = async () => {
			try {
				// Generate last 7 days excluding today
				const last7Days = Array.from({ length: 7 }, (_, i) => {
					const date = new Date();
					date.setDate(date.getDate() - (7 - i));
					return {
						date: date.toISOString().split('T')[0],
						day: date.toLocaleDateString('en-US', { weekday: 'short' }),
						sales: 0,
						customers: 0,
					};
				});

				const response = await getWeeklyDailySummaries(workplaceId);

				// Merge API data with the 7-day template
				const mergedData = last7Days.map((day) => {
					const apiData = response.member?.find((item) => item.date === day.date);
					return {
						day: day.day,
						sales: apiData?.salesTotal || 0,
						customers: apiData?.trafficTotal || 0,
					};
				});

				setSalesData(mergedData);
			} catch (error) {
				console.error('Error fetching sales data:', error);
				setSalesData(createDefaultSalesData());
			}
		};

		fetchSalesData();
	}, [workplaceId, canViewSalesChart]);

	// Check permissions
	if (!canViewDashboard) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '400px',
					p: 3,
				}}
			>
				<Typography variant="h6" color="textSecondary">
					You do not have permission to view Home.
				</Typography>
			</Box>
		);
	}

	// Show event detail view if an event is selected
	if (showEventDetail && selectedEvent && eventType) {
		return (
			<EventDetailView
				event={selectedEvent}
				onClose={handleCloseEventDetail}
				eventType={eventType}
			/>
		);
	}

	return (
		<div className="dashboard-container">
			{/* Top Section */}
			<div className="top-section">
				<Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
					{canViewSalesChart && (
					<Grid item xs={12} md={8} className="dashboard-sales-chart-column">
						<div className="card sales-chart">
							<Typography variant="h6" className="dashboard-card-title">
								Rolling 7 Days
							</Typography>
							<div className="dashboard-charts-container">
								<div className="dashboard-chart-section">
									<Typography variant="h6" className="dashboard-chart-title">
										Daily Sales ($)
									</Typography>
									<div className="dashboard-chart-wrapper">
										<ResponsiveContainer width="100%" height={250}>
											<BarChart
												data={salesData}
												margin={{
													top: 30,
													right: 30,
													left: 20,
													bottom: 5,
												}}
											>
												<XAxis dataKey="day" />
												<YAxis />
												<Tooltip />
												<Bar dataKey="sales" fill="#436183">
													<LabelList
														dataKey="sales"
														position="top"
														formatter={(value) => `$${value ?? 0}`}
														style={{ fontSize: '12px' }}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								</div>

								<div className="dashboard-chart-section">
									<Typography variant="h6" className="dashboard-chart-title">
										Daily Customers
									</Typography>
									<div className="dashboard-chart-wrapper">
										<ResponsiveContainer width="100%" height={250}>
											<BarChart
												data={salesData}
												margin={{
													top: 30,
													right: 30,
													left: 20,
													bottom: 5,
												}}
											>
												<XAxis dataKey="day" />
												<YAxis />
												<Tooltip />
												<Bar dataKey="customers" fill="#436183">
													<LabelList
														dataKey="customers"
														position="top"
														formatter={(value) => value ?? 0}
														style={{ fontSize: '12px' }}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								</div>
							</div>
						</div>
					</Grid>
					)}
					{(canViewLocalEvents || canViewHotGames) && (
					<Grid item xs={12} md={canViewSalesChart ? 4 : 12}>
						<Grid
							container
							direction="column"
							spacing={2}
							className="dashboard-side-stack"
						>
							<Grid item xs={6}>
								{canViewLocalEvents && (
								<div className="card alerts-dashboard">
									<div className="events-header">
										<Typography variant="h6" className="dashboard-card-title">
											Mabel A.I. - Local Events
										</Typography>
									</div>
									<LocalEvents workplaceId={workplaceId} onEventClick={handleLocalEventClick} />
								</div>
								)}
							</Grid>
							<Grid item xs={6}>
								{canViewHotGames && (
								<HotGames onGameClick={handleHotGameClick} />
								)}
							</Grid>
						</Grid>
					</Grid>
					)}
				</Grid>
			</div>

			{/* Middle Section */}
			{(canViewChecklistWidget || canViewTodoWidget) && (
			<div className="middle-section">
				{canViewChecklistWidget && (
				<TableContainer component={Paper} className="card">
					<Typography variant="h6" className="dashboard-card-title" sx={{ px: 2 }}>
					All Outstanding Checklists
					</Typography>
					<div className="dashboard-table-scroll">
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Checklist Name
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Assigned
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Owner</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Checklist Due
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={4} align="center">
										<CircularProgress size={20} />
									</TableCell>
								</TableRow>
							) : checklistsToShow.length > 0 ? (
								checklistsToShow.map((checklist) => (
									<TableRow
										key={checklist.id}
										sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
									>
										<TableCell>{checklist.checklistTemplate.label}</TableCell>
										<TableCell>{getChecklistAssignedDisplay(checklist)}</TableCell>
										<TableCell>{getChecklistOwnerDisplay(checklist)}</TableCell>
									<TableCell>
										{checklist.scheduledDateFormatted && checklist.scheduledTimeFormatted
											? formatDateTime12Hour(checklist.scheduledDateFormatted, checklist.scheduledTimeFormatted)
											: formatDateTime(checklist.scheduledDate)}
									</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={4} align="center">
										No outstanding checklists
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
					</div>
				</TableContainer>
				)}

				{canViewTodoWidget && (
				<TableContainer component={Paper} className="card">
					<Typography variant="h6" className="dashboard-card-title" sx={{ px: 2 }}>
					All Outstanding To-Dos
					</Typography>
					<div className="dashboard-table-scroll">
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Description
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '18%' }}>
									Assigned
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '14%' }}>
									Category
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
									Status
								</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '16%', whiteSpace: 'nowrap' }}>
									Due Date
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={5} align="center">
										<CircularProgress size={20} />
									</TableCell>
								</TableRow>
						) : allOutstandingTodos.length > 0 ? (
							allOutstandingTodos.map((todo) => (
									<TableRow
										key={todo.id}
										sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
									>
										<TableCell>{getDescriptionDisplay(todo)}</TableCell>
										<TableCell>{getTodoAssignedDisplay(todo)}</TableCell>
										<TableCell>{getTodoCategoryDisplay(todo)}</TableCell>
										<TableCell>{getTodoStatusDisplay(todo)}</TableCell>
										<TableCell>{formatTodoDateWithTime(todo)}</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={5} align="center">
										No outstanding to-dos
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
					</div>
				</TableContainer>
				)}
			</div>
			)}
		</div>
	);
};

export default Dashboard;
